import { TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import { useState, useEffect, useCallback } from 'react';
import { Button } from 'src/components/button';
import FormInput from 'src/components/form-input';
import { IUserCredential } from 'src/context/global/types';
import { EEvents, ReplaceUserCredentialsEvent } from 'src/types/events';
import { createPasswordHash } from 'src/utils/password-hash';
import { twMerge } from 'tailwind-merge';

import FormSelect from '../form-select';

const getLevelLabel = (level: number) => {
  switch (level) {
    case 0:
      return 'Admin';
    case 1:
      return 'Read-only';
    default:
      return 'N/A';
  }
};

// Username validation: only alphanumeric characters and underscores, no spaces or special characters
const validateUsername = (username: string): string | null => {
  if (!username.trim()) {
    return 'Username is required';
  }

  if (username.length < 3) {
    return 'Username must be at least 3 characters long';
  }

  if (username.length > 20) {
    return 'Username must be no more than 20 characters long';
  }

  // Only allow alphanumeric characters and underscores
  const validUsernameRegex = /^[a-zA-Z0-9_]+$/;
  if (!validUsernameRegex.test(username)) {
    return 'Username can only contain letters, numbers, and underscores (no spaces or special characters)';
  }

  return null;
};

interface UserCredentialsManagerProps {
  userCredentials: IUserCredential[];
  onUserCredentialsChange: (credentials: IUserCredential[]) => void;
  className?: string;
}

export default function UserCredentialsManager({
  userCredentials,
  onUserCredentialsChange,
  className,
}: UserCredentialsManagerProps) {
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [newUser, setNewUser] = useState({ name: '', password: '', level: 0 });
  const [editUser, setEditUser] = useState({
    name: '',
    password: '',
    level: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [usernameError, setUsernameError] = useState<string>('');

  const handleAddUser = async () => {
    if (!newUser.name.trim() || !newUser.password.trim()) {
      setError('Username and password are required');
      return;
    }

    // Validate username format
    const usernameValidationError = validateUsername(newUser.name.trim());
    if (usernameValidationError) {
      setError(usernameValidationError);
      return;
    }

    if (userCredentials.length >= 10) {
      setError('Maximum 10 users allowed');
      return;
    }

    if (userCredentials.some((user) => user.name === newUser.name.trim())) {
      setError('Username already exists');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { hash, salt } = await createPasswordHash(
        newUser.name.trim(),
        newUser.password,
      );

      const credential: IUserCredential = {
        id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: newUser.name.trim(),
        passwordHash: hash,
        salt,
        level: newUser.level, // Default level for new users
      };

      onUserCredentialsChange([...userCredentials, credential]);
      setNewUser({ name: '', password: '', level: 0 });
      setIsAddingUser(false);
    } catch (err) {
      setError('Failed to create user');
      // eslint-disable-next-line no-console
      console.error('Error creating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!editUser.name.trim()) {
      setError('Username is required');
      return;
    }

    // Validate username format
    const usernameValidationError = validateUsername(editUser.name.trim());
    if (usernameValidationError) {
      setError(usernameValidationError);
      return;
    }

    if (editingUserId) {
      const existingUser = userCredentials.find(
        (user) =>
          user.name === editUser.name.trim() && user.id !== editingUserId,
      );
      if (existingUser) {
        setError('Username already exists');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const updatedCredentials = userCredentials.map((user) => {
          if (user.id === editingUserId) {
            if (editUser.password.trim()) {
              // If password is provided, update password hash and salt
              return createPasswordHash(
                editUser.name.trim(),
                editUser.password,
              ).then(({ hash, salt }) => ({
                ...user,
                name: editUser.name.trim(),
                passwordHash: hash,
                level: editUser.level,
                salt,
              }));
            }
            // If no password provided, only update name
            return Promise.resolve({
              ...user,
              name: editUser.name.trim(),
              level: editUser.level,
            });
          }
          return Promise.resolve(user);
        });

        const resolvedCredentials = await Promise.all(updatedCredentials);
        onUserCredentialsChange(resolvedCredentials);
        setEditingUserId(null);
        setEditUser({ name: '', password: '', level: 0 });
      } catch (err) {
        setError('Failed to update user');
        // eslint-disable-next-line no-console
        console.error('Error updating user:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStartEdit = (user: IUserCredential) => {
    setEditingUserId(user.id);
    setEditUser({ name: user.name, password: '', level: user.level || 0 });
    setError('');
    setUsernameError('');
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditUser({ name: '', password: '', level: editUser.level || 0 });
    setError('');
    setUsernameError('');
  };

  const handleDeleteUser = (userId: string) => {
    if (userCredentials.length <= 1) {
      setError('At least one user must exist');
      return;
    }

    onUserCredentialsChange(
      userCredentials.filter((user) => user.id !== userId),
    );
    setError('');
  };

  const handleCancelAdd = () => {
    setIsAddingUser(false);
    setNewUser({ name: '', password: '', level: newUser.level || 0 });
    setError('');
    setUsernameError('');
  };

  const handleNewUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setNewUser({ ...newUser, name: value });

    // Clear general error when user starts typing
    if (error) setError('');

    // Validate username in real-time
    if (value.trim()) {
      const validationError = validateUsername(value.trim());
      setUsernameError(validationError || '');
    } else {
      setUsernameError('');
    }
  };

  const handleEditUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEditUser({ ...editUser, name: value });

    // Clear general error when user starts typing
    if (error) setError('');

    // Validate username in real-time
    if (value.trim()) {
      const validationError = validateUsername(value.trim());
      setUsernameError(validationError || '');
    } else {
      setUsernameError('');
    }
  };

  // Create event for replace user credentials
  const handleReplaceUserCredentials = useCallback(
    (event: CustomEvent<ReplaceUserCredentialsEvent>) => {
      onUserCredentialsChange(event.detail.userCredentials);
    },
    [onUserCredentialsChange],
  );

  // listen to replace user credentials event
  useEffect(() => {
    window.addEventListener(
      EEvents.REPLACE_USER_CREDENTIALS,
      handleReplaceUserCredentials as EventListener,
    );
    return () => {
      window.removeEventListener(
        EEvents.REPLACE_USER_CREDENTIALS,
        handleReplaceUserCredentials as EventListener,
      );
    };
  }, [handleReplaceUserCredentials]);

  return (
    <div
      data-testid="user-credentials-manager"
      className={twMerge('space-y-4', className)}
    >
      <div
        data-testid="user-credentials-manager-title"
        className="border-t border-gray-600 pt-2"
      >
        <h2 className="text-base font-semibold text-gray-900">
          User Credentials
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage user accounts for ROQSTAR device configuration files. Maximum
          10 users, minimum 1 user.
        </p>
      </div>

      {(error || usernameError) && (
        <div
          data-testid="user-credentials-manager-error"
          className="rounded-md border border-red-200 bg-red-50 p-3"
        >
          {error && <p className="text-sm text-red-600">{error}</p>}
          {usernameError && (
            <p className="text-sm text-red-600">{usernameError}</p>
          )}
        </div>
      )}

      {/* Current Users List */}
      <div
        data-testid="user-credentials-manager-users-list"
        className="rounded-lg bg-gray-50 p-4"
      >
        <h3 className="mb-3 text-sm font-medium text-gray-900">
          Current Users ({userCredentials.length}/10)
        </h3>

        {userCredentials.length === 0 ? (
          <p
            data-testid="user-credentials-manager-users-list-empty"
            className="text-sm italic text-gray-500"
          >
            No users defined
          </p>
        ) : (
          <div className="space-y-2">
            {userCredentials.map((user) => (
              <div
                data-testid={`user-credentials-manager-wrapper-user-${user.name}`}
                key={user.name}
              >
                {editingUserId === user.id ? (
                  /* Edit User Form */
                  <div
                    data-testid={`user-credentials-manager-user-edit-form-${user.name}`}
                    className="rounded-md border border-blue-200 bg-blue-50 p-4"
                  >
                    <h4 className="mb-3 text-sm font-medium text-blue-900">
                      Edit User: {user.name}
                    </h4>
                    <div className="space-y-3">
                      <FormInput
                        data-testid={`user-credentials-manager-user-edit-form-username-${user.name}`}
                        id={`edit-username-${user.name}`}
                        name="username"
                        label="Username (only letters, numbers, and underscores)"
                        value={editUser.name}
                        onChange={handleEditUserNameChange}
                        disabled={loading}
                        error={usernameError}
                      />
                      <FormInput
                        id={`edit-password-${user.name}`}
                        data-testid={`user-credentials-manager-user-edit-form-password-${user.name}`}
                        name="password"
                        label="New Password (leave empty to keep current)"
                        type="password"
                        value={editUser.password}
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            password: e.target.value,
                          })
                        }
                        placeholder="Enter new password (optional)"
                        disabled={loading}
                      />
                      <FormSelect
                        id={`edit-level-${user.name}`}
                        data-testid={`user-credentials-manager-user-edit-form-level-${user.name}`}
                        name="level"
                        label="Level"
                        value={editUser.level?.toString() || '0'}
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            level: Number(e.target.value),
                          })
                        }
                        options={[
                          { label: 'Admin', value: '0' },
                          { label: 'Read-only', value: '1' },
                        ]}
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          data-testid={`user-credentials-manager-user-edit-form-update-button-${user.name}`}
                          onClick={handleEditUser}
                          disabled={
                            loading || !editUser.name.trim() || !!usernameError
                          }
                          className="flex-1"
                        >
                          {loading ? 'Updating...' : 'Update User'}
                        </Button>
                        <Button
                          type="button"
                          color="gray"
                          data-testid={`user-credentials-manager-user-edit-form-cancel-button-${user.name}`}
                          onClick={handleCancelEdit}
                          disabled={loading}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Normal User Display */
                  <div
                    data-testid={`user-credentials-manager-user-${user.name}`}
                    className="flex items-center justify-between rounded-md border bg-white p-3"
                  >
                    <div>
                      <span className="font-medium text-gray-900">
                        {user.name}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {getLevelLabel(user.level)}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        color="blue"
                        data-testid={`user-credentials-manager-user-edit-button-${user.name}`}
                        onClick={() => handleStartEdit(user)}
                        disabled={
                          loading || isAddingUser || editingUserId !== null
                        }
                        className="text-xs"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        color="red"
                        data-testid={`user-credentials-manager-user-delete-button-${user.name}`}
                        onClick={() => handleDeleteUser(user.name)}
                        disabled={
                          userCredentials.length <= 1 ||
                          loading ||
                          editingUserId !== null
                        }
                        className="text-xs"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add User Section */}
      {!isAddingUser ? (
        <Button
          type="button"
          color="blue"
          data-testid="user-credentials-manager-add-user-button"
          onClick={() => setIsAddingUser(true)}
          disabled={userCredentials.length >= 10 || editingUserId !== null}
          className="w-full"
        >
          Add User
        </Button>
      ) : (
        <div
          data-testid="user-credentials-manager-add-user-form"
          className="rounded-lg border border-blue-200 bg-blue-50 p-4"
        >
          <h3 className="mb-3 text-sm font-medium text-blue-900">
            Add New User
          </h3>
          <div className="space-y-3">
            <FormInput
              id="new-username"
              data-testid="user-credentials-manager-add-user-form-username-input"
              name="username"
              label="Username (only letters, numbers, and underscores)"
              value={newUser.name}
              onChange={handleNewUserNameChange}
              placeholder="Enter username (3-20 characters)"
              disabled={loading}
              error={usernameError}
            />
            <FormInput
              id="new-password"
              data-testid="user-credentials-manager-add-user-form-password-input"
              name="password"
              label="Password"
              type="password"
              value={newUser.password}
              onChange={(e) =>
                setNewUser({ ...newUser, password: e.target.value })
              }
              placeholder="Enter password"
              disabled={loading}
            />
            <FormSelect
              id="new-level"
              data-testid="user-credentials-manager-add-user-form-level-input"
              name="level"
              label="Level"
              value={newUser.level?.toString() || '0'}
              onChange={(e) =>
                setNewUser({ ...newUser, level: Number(e.target.value) })
              }
              options={[
                { label: getLevelLabel(0), value: '0' },
                { label: getLevelLabel(1), value: '1' },
              ]}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="user-credentials-manager-add-user-form-create-button"
                onClick={handleAddUser}
                disabled={
                  loading ||
                  !newUser.name.trim() ||
                  !newUser.password.trim() ||
                  !!usernameError
                }
                className="flex-1"
              >
                {loading ? 'Creating...' : 'Create User'}
              </Button>
              <Button
                type="button"
                color="gray"
                data-testid="user-credentials-manager-add-user-form-cancel-button"
                onClick={handleCancelAdd}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
